import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { readRealtimeConfig } from './config';
import { normalizeResources } from './resources';
import { createInvalidationCoordinator } from './invalidationCoordinator';
import { createGatewayClient, createTokenProvider } from './gateway';
import { createRealtimeRuntime } from './runtime';
import { createPilotRuntime } from './pilotRuntime';
import { resumeBackgroundRequests } from '../redux/api/portalApi';

const noop = () => {};
const disabledContext = { enabled: false, register: () => noop, resume: noop, status: 'disabled', pending: 0, refreshing: 0 };
const RealtimeContext = createContext(disabledContext);

// The app supplies the existing pilot transport; V2 is a separate explicit opt-in.
// Children always render, including when configuration, authentication or the gateway fails.
export function RealtimeProvider({ children, configuration, instance, account, pilotTransport, pilotStatus }) {
  const config = useMemo(() => {
    try { return configuration || readRealtimeConfig(); }
    catch { return { enabled: false, invalid: true }; }
  }, [configuration]);
  const usePilot = !config.enabled && !config.invalid && Boolean(pilotTransport);
  const [state, setState] = useState({ status: 'disabled', pending: 0, refreshing: 0 });
  const bundle = useRef(null);
  const registrations = useRef(new Map());
  const register = useCallback(options => {
    const token = Symbol('registration');
    const entry = { options, release: bundle.current?.coordinator.register(options) };
    registrations.current.set(token, entry);
    return () => { entry.release?.(); registrations.current.delete(token); };
  }, []);
  const resume = useCallback(() => bundle.current?.coordinator.resume(), []);

  useEffect(() => {
    if ((!config.enabled && !usePilot) || !account) {
      setState({ status: config.invalid ? 'configuration-error' : config.enabled ? 'signed-out' : 'disabled', pending: 0, refreshing: 0 });
      return undefined;
    }
    let disposed = false;
    const update = patch => { if (!disposed) setState(current => ({ ...current, ...patch })); };
    let runtime;
    const coordinator = createInvalidationCoordinator({ environment: usePilot ? pilotTransport.environment : config.environment,
      isVisible: () => document.visibilityState !== 'hidden',
      onResourcesChanged: resources => runtime?.setResources(resources), onState: update });
    const entries = registrations.current;
    try {
      if (usePilot) {
        runtime = createPilotRuntime({ coordinator, transport: pilotTransport, onStatus: status => update({ status }) });
      } else {
        const getToken = createTokenProvider(instance, account, config.scope);
        const gateway = createGatewayClient(config, getToken);
        runtime = createRealtimeRuntime({ config, coordinator, gateway, getToken, onStatus: status => update({ status }) });
      }
      bundle.current = { coordinator, runtime };
      entries.forEach(entry => { entry.release = coordinator.register(entry.options); });
      runtime.start();
    } catch {
      bundle.current = null;
      runtime?.dispose(); coordinator.dispose();
      entries.forEach(entry => { entry.release = undefined; });
      update({ status: 'unavailable', pending: 0, refreshing: 0 });
      return () => { disposed = true; };
    }
    const recover = () => { if (document.visibilityState !== 'hidden') { resumeBackgroundRequests(); runtime.resume(); coordinator.resume(); } };
    window.addEventListener('online', recover);
    document.addEventListener('visibilitychange', recover);
    return () => {
      disposed = true; bundle.current = null;
      window.removeEventListener('online', recover);
      document.removeEventListener('visibilitychange', recover);
      runtime.dispose(); coordinator.dispose();
      entries.forEach(entry => { entry.release = undefined; });
    };
  }, [config, instance, account, usePilot, pilotTransport]);
  useEffect(() => { bundle.current?.runtime.setStatus?.(pilotStatus); }, [pilotStatus, account, usePilot, pilotTransport]);
  const enabled = Boolean((config.enabled || usePilot) && account);
  const mode = config.enabled ? 'session' : usePilot ? pilotTransport.subscribeDomain ? 'domain' : 'pilot' : 'disabled';
  const value = useMemo(() => ({ ...state, enabled, mode, register, resume }), [state, enabled, mode, register, resume]);
  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

export const useRealtimeState = () => useContext(RealtimeContext);

// The caller supplies the existing refresh owner; no Redux/cache policy is changed here.
export function useRealtimeResource({ enabled = false, ownerKey, resources, refresh, blocked = false, freshSince = 0 }) {
  const { register, resume, enabled: providerEnabled } = useRealtimeState();
  const current = useRef({ refresh, blocked });
  current.current = { refresh, blocked };
  const active = enabled && providerEnabled;
  const resourceKey = active ? JSON.stringify(normalizeResources(resources || [])) : '[]';
  useEffect(() => {
    if (!active) return undefined;
    return register({ key: ownerKey, resources: JSON.parse(resourceKey), freshSince,
      refresh: args => current.current.refresh(args), isBlocked: () => current.current.blocked });
  }, [active, ownerKey, resourceKey, register, freshSince]);
  useEffect(() => { if (active && !blocked) resume(); }, [active, blocked, resume]);
}
